import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Layout } from "@/components/Layout";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Invoices from "@/pages/Invoices";
import SOPs from "@/pages/SOPs";
import CompoundingLogs from "@/pages/CompoundingLogs";
import Compliance from "@/pages/Compliance";
import Licenses from "@/pages/Licenses";
import Employees from "@/pages/Employees";
import TimeClock from "@/pages/TimeClock";
import PharmacyProfile from "@/pages/PharmacyProfile";
import UserManagement from "@/pages/UserManagement";
import Reports from "@/pages/Reports";
import TrainingMatrix from "@/pages/TrainingMatrix";
import WorkOrders from "@/pages/WorkOrders";
import COALibrary from "@/pages/COALibrary";
import Expenses from "@/pages/Expenses";
import TimeclockStaff from "@/pages/TimeclockStaff";
import Contacts from "@/pages/Contacts";
import Credentials from "@/pages/Credentials";
import ClockIn from "@/pages/ClockIn";
import AuditLog from "@/pages/AuditLog";
import PatientOrders from "@/pages/PatientOrders";
import UpsShipments from "@/pages/UpsShipments";
import ScannedInvoices from "@/pages/ScannedInvoices";
import Formulas from "@/pages/Formulas";
import FormulaDetail from "@/pages/FormulaDetail";
import Inventory from "@/pages/Inventory";
import BatchLogs from "@/pages/BatchLogs";
import BatchLogDetail from "@/pages/BatchLogDetail";
import { Loader2 } from "lucide-react";

const Protected = ({ children }) => {
  const { user } = useAuth();
  if (user === undefined) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-pink-700" /></div>;
  if (user === null) return <Navigate to="/login" replace />;
  return <Layout>{children}</Layout>;
};

const AdminOnly = ({ children }) => {
  const { user } = useAuth();
  if (user === undefined) return null;
  if (user === null) return <Navigate to="/login" replace />;
  if (user.role !== "admin") return <Navigate to="/" replace />;
  return <Layout>{children}</Layout>;
};

const LoginRoute = () => {
  const { user } = useAuth();
  if (user) return <Navigate to="/" replace />;
  return <Login />;
};

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<LoginRoute />} />
            <Route path="/clockin" element={<ClockIn />} />
            <Route path="/timeclock-staff" element={<Navigate to="/employees" replace />} />
            <Route path="/audit-log" element={<AdminOnly><AuditLog /></AdminOnly>} />
            <Route path="/" element={<Protected><Dashboard /></Protected>} />
            <Route path="/invoices" element={<Protected><Invoices /></Protected>} />
            <Route path="/sops" element={<Protected><SOPs /></Protected>} />
            <Route path="/compounding-logs" element={<Protected><CompoundingLogs /></Protected>} />
            <Route path="/coa-library" element={<Protected><COALibrary /></Protected>} />
            <Route path="/work-orders" element={<Protected><WorkOrders /></Protected>} />
            <Route path="/expenses" element={<Protected><Expenses /></Protected>} />
            <Route path="/compliance" element={<Protected><Compliance /></Protected>} />
            <Route path="/training-matrix" element={<Protected><TrainingMatrix /></Protected>} />
            <Route path="/patient-orders" element={<Protected><PatientOrders /></Protected>} />
            <Route path="/ups-shipments" element={<Protected><UpsShipments /></Protected>} />
            <Route path="/scanned-invoices" element={<Protected><ScannedInvoices /></Protected>} />
            <Route path="/formulas" element={<Protected><Formulas /></Protected>} />
            <Route path="/formulas/:id" element={<Protected><FormulaDetail /></Protected>} />
            <Route path="/inventory" element={<Protected><Inventory /></Protected>} />
            <Route path="/batch-logs" element={<Protected><BatchLogs /></Protected>} />
            <Route path="/batch-logs/:id" element={<Protected><BatchLogDetail /></Protected>} />
            <Route path="/reports" element={<Protected><Reports /></Protected>} />
            <Route path="/licenses" element={<Protected><Licenses /></Protected>} />
            <Route path="/employees" element={<Protected><Employees /></Protected>} />
            <Route path="/time-clock" element={<Protected><TimeClock /></Protected>} />
            <Route path="/pharmacy-profile" element={<Protected><PharmacyProfile /></Protected>} />
            <Route path="/contacts" element={<AdminOnly><Contacts /></AdminOnly>} />
            <Route path="/vault" element={<AdminOnly><Credentials /></AdminOnly>} />
            <Route path="/users" element={<AdminOnly><UserManagement /></AdminOnly>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </div>
  );
}

export default App;
